import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
  Typography,
  CircularProgress,
  Chip,
  InputAdornment,
} from '@mui/material';
import { toast } from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { QUERY_SOURCES } from 'src/constants/config';
import { queryApi } from 'src/api/queryApi';
import { useDecodedToken } from 'src/hooks/useDecodedToken';
import { addToQueue } from 'src/store/slices/queueSlice';
import { formatDateTimeForServer, toDatetimeLocal, formatDateToInput } from 'src/utils/dateUtils';
import { parseQueryNotes, createQueryNotesJson } from 'src/utils/queryNotesUtils';

const SOURCE_OPTIONS = Object.entries(QUERY_SOURCES).map(([value, label]) => ({ value, label }));

const QueryFormModal = ({ open, onClose, query = null, onSuccess }) => {
  const { decodedToken } = useDecodedToken();
  const dispatch = useDispatch();
  const isEditMode = Boolean(query);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'new',
    source: '',
    topics: '',
    queryURL: '',
    mediaOutlet: '',
    dueDate: '',
    expertsID: '',
    emailSentOut: '',
    userClaimID: '',
    notes: '',
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [topicsInput, setTopicsInput] = useState('');
  const [topicsList, setTopicsList] = useState([]);

  const requiredFields = ['title', 'description', 'source', 'topics', 'dueDate', 'mediaOutlet'];

  const validate = (values) => {
    const newErrors = {};
    if (!values.title || !values.title.trim()) newErrors.title = 'Title is required';
    if (!values.description || !values.description.trim()) newErrors.description = 'Description is required';
    // Status is always 'draft' by default, no validation needed
    if (!values.source || !values.source.trim()) newErrors.source = 'Source is required';
    if (!topicsList || topicsList.length === 0) newErrors.topics = 'At least one topic is required';
    if (!values.dueDate || !values.dueDate.toString().trim()) newErrors.dueDate = 'Due date is required';
    if (!values.mediaOutlet || !values.mediaOutlet.trim()) newErrors.mediaOutlet = 'Media outlet is required';
    return newErrors;
  };

  useEffect(() => {
    if (query) {
      // Parse the notes field to extract just the notes portion
      const parsedNotes = parseQueryNotes(query.notes);

      setFormData({
        title: query.title || '',
        description: query.description || '',
        status: query.status || 'new',
        source: (() => {
          const key = (query.source || '').toString().trim().toLowerCase();
          if (QUERY_SOURCES[key]) return key;
          // try to map from legacy label to key
          const fromLabel = Object.entries(QUERY_SOURCES).find(([, label]) => label.toLowerCase() === key);
          return fromLabel ? fromLabel[0] : 'other';
        })(),
        topics: query.topics || '',
        queryURL: query.queryURL || '',
        mediaOutlet: query.mediaOutlet || '',
        dueDate: toDatetimeLocal(query.dueDate),
        expertsID: '',
        emailSentOut: '',
        userClaimID: decodedToken?.userID || '',
        notes: parsedNotes.notes,
      });
      const normalizeToHashtags = (input) => {
        const normalizeTag = (raw) => {
          const cleaned = (raw || '')
            .toString()
            .trim()
            .replace(/^['"]+|['"]+$/g, '');
          if (!cleaned) return undefined;
          const noHash = cleaned.replace(/^#+/, '');
          if (!noHash) return undefined;
          return `#${noHash}`;
        };

        if (!input) return [];

        // Arrays from API
        if (Array.isArray(input)) {
          return input.map((t) => normalizeTag(t)).filter(Boolean);
        }

        const str = (input || '').toString().trim();
        if (!str) return [];

        // Prefer JSON parsing if possible (handles ["#tag", "tag2"]) nicely
        try {
          const jsonish = str.replace(/'/g, '"');
          const parsed = JSON.parse(jsonish);
          if (Array.isArray(parsed)) {
            return parsed.map((t) => normalizeTag(t)).filter(Boolean);
          }
        } catch (_) {
          /* ignore parse errors */
        }

        // Fallback: content inside brackets, split by comma
        const bracketMatch = str.match(/\[(.*)\]/);
        if (bracketMatch) {
          const inner = bracketMatch[1];
          return inner
            .split(',')
            .map((t) => normalizeTag(t))
            .filter(Boolean);
        }

        // Final fallback: comma-separated string
        return str
          .split(',')
          .map((t) => normalizeTag(t))
          .filter(Boolean);
      };
      setTopicsList(normalizeToHashtags(query.topics));
      setTopicsInput('');
    } else {
      const now = new Date();
      const endOfTodayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 0, 0);
      setFormData({
        title: '',
        description: '',
        status: 'new',
        source: '',
        topics: '',
        queryURL: '',
        mediaOutlet: '',
        dueDate: formatDateToInput(endOfTodayLocal),
        expertsID: '',
        emailSentOut: '',
        userClaimID: decodedToken?.userID || '',
        notes: '',
      });
      setTopicsList([]);
      setTopicsInput('');
    }
  }, [query, decodedToken?.userID]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value,
    });

    // Clear error for this field as user types if it becomes valid
    if (errors[field]) {
      const fieldHasValue = typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
      if (fieldHasValue) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (requiredFields.includes(field)) {
      const value = formData[field];
      const fieldEmpty = !value || (typeof value === 'string' && value.trim().length === 0);
      setErrors((prev) => ({
        ...prev,
        [field]:
          field === 'topics'
            ? topicsList.length === 0
              ? 'At least one topic is required'
              : undefined
            : field === 'dueDate'
            ? fieldEmpty
              ? 'Due date is required'
              : undefined
            : field === 'mediaOutlet'
            ? fieldEmpty
              ? 'Media outlet is required'
              : undefined
            : fieldEmpty
            ? `${field.charAt(0).toUpperCase() + field.slice(1)} is required`
            : undefined,
      }));
    }
  };

  const addTopic = () => {
    const raw = (topicsInput || '').trim();
    if (!raw) return;
    const normalized = `#${raw.replace(/^#+/, '').replace(/\s+/g, '')}`;
    if (topicsList.some((t) => t.toLowerCase() === normalized.toLowerCase())) {
      setTopicsInput('');
      return;
    }
    setTopicsList((prev) => [...prev, normalized]);
    setTopicsInput('');
    setErrors((prev) => ({ ...prev, topics: undefined }));
  };

  const removeTopic = (tag) => {
    setTopicsList((prev) => prev.filter((t) => t !== tag));
  };

  const handleSubmit = async () => {
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Mark all required fields as touched to reveal errors
      setTouched((prev) => ({
        ...prev,
        title: true,
        description: true,
        // status: true, // Status field is commented out
        source: true,
        topics: true,
      }));

      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Create the notes JSON structure
      const notesData = {
        notes: formData.notes || '',
        dateWriting: null,
        reportDate: null,
        draftCompany: null,
      };

      // In edit mode, preserve existing dateWriting, reportDate, and draftCompany
      if (isEditMode && query.notes) {
        const existingNotes = parseQueryNotes(query.notes);
        notesData.dateWriting = existingNotes.dateWriting;
        notesData.reportDate = existingNotes.reportDate;
        notesData.draftCompany = existingNotes.draftCompany;
        // Override notes text with current form value
        notesData.notes = formData.notes || '';
      }

      const params = {
        title: formData.title,
        description: formData.description,
        source: formData.source,
        topics: JSON.stringify(topicsList),
        notes: createQueryNotesJson(notesData),
        ...(formData.queryURL && { queryURL: formData.queryURL }),
        ...(formData.mediaOutlet && { mediaOutlet: formData.mediaOutlet }),
        ...(formData.dueDate && { dueDate: formatDateTimeForServer(formData.dueDate) }),
      };

      if (isEditMode) {
        await queryApi.updateQueryV2({
          ...params,
          queryID: query.id,
        });
        toast.success('Query updated successfully');
      } else {
        const createParams = {
          ...params,
          status: 'draft',
          userClaimID: Number(decodedToken?.userID),
          createdBy: decodedToken?.userID,
        };
        const createdQuery = await queryApi.createQuery(createParams);

        // Add the created query to the queue
        if (createdQuery && createdQuery.id) {
          dispatch(
            addToQueue({
              queryID: Number(createdQuery.id),
              title: formData.title,
              mediaOutlet: formData.mediaOutlet,
              dueDate: formData.dueDate,
            })
          );
        }

        toast.success('Query created successfully');
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      toast.error(isEditMode ? 'Failed to update query' : 'Failed to create query');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Query' : 'Create New Query'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Title"
            value={formData.title}
            onChange={handleChange('title')}
            onBlur={handleBlur('title')}
            fullWidth
            required
            variant="outlined"
            error={Boolean(touched.title && errors.title)}
            helperText={touched.title && errors.title ? errors.title : ''}
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={handleChange('description')}
            multiline
            rows={4}
            fullWidth
            required
            variant="outlined"
            onBlur={handleBlur('description')}
            error={Boolean(touched.description && errors.description)}
            helperText={touched.description && errors.description ? errors.description : ''}
          />

          {/* Status field commented out - always defaults to 'draft' */}
          {/* <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Status"
              value={formData.status}
              onChange={handleChange('status')}
              onBlur={handleBlur('status')}
              select
              fullWidth
              required
              variant="outlined"
              error={Boolean(touched.status && errors.status)}
              helperText={touched.status && errors.status ? errors.status : ''}
            >
              {Object.entries(QUERY_STATUSES).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value.label}
                </MenuItem>
              ))}
            </TextField> */}

          <TextField
            label="Source"
            value={formData.source}
            onChange={handleChange('source')}
            onBlur={handleBlur('source')}
            select
            fullWidth
            required
            variant="outlined"
            error={Boolean(touched.source && errors.source)}
            helperText={touched.source && errors.source ? errors.source : ''}
          >
            {SOURCE_OPTIONS.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
          {/* </Box> */}

          <TextField
            label="Query URL (Optional)"
            value={formData.queryURL}
            onChange={handleChange('queryURL')}
            fullWidth
            variant="outlined"
            placeholder="https://qwoted.com/query/123"
          />

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="Topic"
              value={topicsInput}
              onChange={(e) => setTopicsInput(e.target.value)}
              onBlur={handleBlur('topics')}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTopic();
                }
              }}
              fullWidth
              required
              variant="outlined"
              error={Boolean(touched.topics && errors.topics)}
              helperText={touched.topics && errors.topics ? errors.topics : 'Add topics like #pr, #testing'}
              InputProps={{
                startAdornment: <InputAdornment position="start">#</InputAdornment>,
              }}
            />
            <Button variant="contained" onClick={addTopic} sx={{ whiteSpace: 'nowrap' }}>
              Add
            </Button>
          </Box>
          {topicsList.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {topicsList.map((tag) => (
                <Chip key={tag} label={tag} onDelete={() => removeTopic(tag)} />
              ))}
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Media outlet"
              value={formData.mediaOutlet}
              onChange={handleChange('mediaOutlet')}
              onBlur={handleBlur('mediaOutlet')}
              fullWidth
              required
              variant="outlined"
              error={Boolean(touched.mediaOutlet && errors.mediaOutlet)}
              helperText={touched.mediaOutlet && errors.mediaOutlet ? errors.mediaOutlet : ''}
            />

            <TextField
              label="Due date and time"
              type="datetime-local"
              value={formData.dueDate}
              onChange={handleChange('dueDate')}
              onBlur={handleBlur('dueDate')}
              fullWidth
              required
              variant="outlined"
              error={Boolean(touched.dueDate && errors.dueDate)}
              helperText={touched.dueDate && errors.dueDate ? errors.dueDate : ''}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>

          <TextField
            label="Notes (Optional)"
            value={formData.notes}
            onChange={handleChange('notes')}
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            placeholder="Add any additional notes here..."
            helperText="These notes will be visible in the query table"
          />

          <Typography variant="caption" color="text.secondary">
            * Required fields: Title, Description, Source, Media outlet, Topics, Due date
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : isEditMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

QueryFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  query: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default QueryFormModal;
